import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import {
  TrendingUp,
  School,
  Group,
  Assessment,
  Visibility,
  Download
} from '@mui/icons-material';
import analyticsService from '../../services/analyticsService';
import { getCurrentSubscription } from '../../services/subscriptionService';
import { Link } from 'react-router-dom';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

const TeacherDashboardAnalytics = ({ demoMode = false }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [timeRange, setTimeRange] = useState(30);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentDetailDialog, setStudentDetailDialog] = useState(false);
  const [selectedStudentData, setSelectedStudentData] = useState(null);
  const [hasAnalyticsAccess, setHasAnalyticsAccess] = useState(false);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);

  const checkSubscriptionAccess = useCallback(async () => {
    try {
      setSubscriptionLoading(true);
      
      // If demo mode is active, grant access immediately
      if (demoMode) {
        setHasAnalyticsAccess(true);
        setSubscriptionLoading(false);
        return;
      }
      
      const subscription = await getCurrentSubscription();
      
      // Check if user has teacher_pro plan
      const hasAccess = subscription && subscription.plan_code === 'teacher_pro';
      setHasAnalyticsAccess(hasAccess);
    } catch (error) {
      console.error('Error checking subscription access:', error);
      setHasAnalyticsAccess(false);
    } finally {
      setSubscriptionLoading(false);
    }
  }, [demoMode]);

  const fetchAnalyticsData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔍 Fetching analytics data with:', { timeRange, demoMode });
      
      // Pass demo mode to the backend so it can return appropriate data
      const data = await analyticsService.getTeacherDashboard(timeRange, demoMode);
      
      console.log('📊 Received analytics data:', data);
      console.log('📊 Demo mode was:', demoMode);
      
      setAnalyticsData(data);
    } catch (err) {
      console.error('❌ Error fetching analytics:', err);
      setError(err.message || 'Failed to fetch analytics data');
    } finally {
      setLoading(false);
    }
  }, [timeRange, demoMode]);

  useEffect(() => {
    checkSubscriptionAccess();
  }, [checkSubscriptionAccess]);

  useEffect(() => {
    if (hasAnalyticsAccess) {
      fetchAnalyticsData();
    }
  }, [timeRange, hasAnalyticsAccess, demoMode, fetchAnalyticsData]);

  // Re-fetch data when demo mode changes
  useEffect(() => {
    if (hasAnalyticsAccess && demoMode) {
      fetchAnalyticsData();
    }
  }, [demoMode, hasAnalyticsAccess, fetchAnalyticsData]);

  const handleStudentClick = async (studentId) => {
    try {
      const studentData = await analyticsService.getStudentPerformance(studentId, timeRange);
      setSelectedStudentData(studentData);
      setStudentDetailDialog(true);
    } catch (err) {
      console.error('Error fetching student details:', err);
    }
  };

  const closeStudentDialog = () => {
    setStudentDetailDialog(false);
    setSelectedStudentData(null);
  };



  if (subscriptionLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  // Check if user has analytics access
  if (!hasAnalyticsAccess) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
          <Box display="flex" flexDirection="column" alignItems="center" textAlign="center" py={8}>
            <Typography variant="h4" component="h1" gutterBottom color="primary">
              Analytics Access Restricted
            </Typography>
            <Typography variant="h6" gutterBottom color="text.secondary" sx={{ mb: 3 }}>
              Upgrade to Teacher Pro plan to access insights
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 600 }}>
              Get detailed class performance analytics, student insights, and subject breakdowns 
              to improve your teaching effectiveness.
            </Typography>
            
            <Box sx={{ mb: 4 }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                component={Link}
                to="/profile"
              >
                Go to Profile → My Subscription
              </Button>
            </Box>

            <Alert severity="info" sx={{ maxWidth: 600 }}>
              <Typography variant="body2">
                <strong>Teacher Pro Plan:</strong> Unlock comprehensive analytics including class performance metrics, 
                individual student insights, subject breakdowns, and performance trends to enhance your teaching effectiveness.
              </Typography>
            </Alert>
          </Box>
        </Container>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      </Container>
    );
  }

  if (!analyticsData) {
    return null;
  }

  // Add safety checks for the expected data structure
  const { 
    class_summary = {}, 
    class_performance = {}, 
    student_performance = [], 
    subject_performance = [], 
    recent_activity = [], 
    performance_trends = [] 
  } = analyticsData;

  // Ensure we have default values for required fields
  const safeClassSummary = {
    total_students: class_summary?.total_students || 0,
    total_tests: class_summary?.total_tests || 0,
    active_subjects: class_summary?.active_subjects || 0,
    date_range_days: class_summary?.date_range_days || timeRange
  };

  const safeClassPerformance = {
    total_attempts: class_performance?.total_attempts || 0,
    pass_rate: class_performance?.pass_rate || 0,
    average_score: class_performance?.average_score || 0,
    total_tests: class_performance?.total_tests || 0
  };

  // Ensure arrays are safe to use
  const safeStudentPerformance = Array.isArray(student_performance) ? student_performance : [];
  const safeSubjectPerformance = Array.isArray(subject_performance) ? subject_performance : [];
  const safeRecentActivity = Array.isArray(recent_activity) ? recent_activity : [];
  const safePerformanceTrends = Array.isArray(performance_trends) ? performance_trends : [];

  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 2, sm: 4 }, mb: 6, px: { xs: 1.5, sm: 2 }, overflowX: 'hidden', minWidth: 0 }}>
                 {/* Header */}
         <Box mb={4}>
           {demoMode && (
             <Alert severity="info" sx={{ mb: 2 }}>
               <Typography variant="body2">
                 <strong>Demo Mode Active:</strong> You are now viewing sample data for demonstration purposes. 
                 Toggle off to view your actual class performance data.
               </Typography>
             </Alert>
           )}
           <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2} flexWrap="wrap">
                         <Box sx={{ minWidth: 0 }}>
               <Typography variant="h5" component="h1" gutterBottom sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                 {demoMode ? 'Demo Teacher Analytics' : 'Teacher Dashboard Analytics'}
               </Typography>
               <Typography variant="subtitle1" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                 {demoMode ? 'demo@teacher.com' : 'Monitor your class performance over the last'} {timeRange} days
               </Typography>
             </Box>
          </Box>
        </Box>

        {/* Time Range Selector */}
      <Box mb={3}>
        <FormControl sx={{ minWidth: { xs: '100%', sm: 200 }, width: { xs: '100%', sm: 'auto' } }}>
          <InputLabel>Time Range</InputLabel>
          <Select
            value={timeRange}
            label="Time Range"
            onChange={(e) => setTimeRange(e.target.value)}
          >
            <MenuItem value={7}>Last 7 days</MenuItem>
            <MenuItem value={14}>Last 14 days</MenuItem>
            <MenuItem value={30}>Last 30 days</MenuItem>
            <MenuItem value={60}>Last 60 days</MenuItem>
            <MenuItem value={90}>Last 90 days</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Class Summary Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Group color="primary" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4" component="div">
                    {safeClassSummary.total_students}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Students
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <School color="success" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4" component="div">
                    {safeClassPerformance.pass_rate.toFixed(1)}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Class Pass Rate
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <TrendingUp color="info" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4" component="div">
                    {safeClassPerformance.average_score.toFixed(1)}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Class Average
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Assessment color="warning" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4" component="div">
                    {safeClassPerformance.total_attempts}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Tests
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Performance Trends and Subject Performance */}
      <Grid container spacing={3} mb={4}>
        {/* Performance Trends */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Performance Trends Over Time
            </Typography>
            {safePerformanceTrends.length === 0 ? (
              <Box textAlign="center" py={4}>
                <Typography variant="body1" color="text.secondary">
                  No performance trend data available for the selected time range.
                </Typography>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={safePerformanceTrends}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis />
                  <Tooltip formatter={(value, name) => [value, name === 'average_score' ? 'Score (%)' : 'Pass Rate (%)']} />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="average_score" 
                    stroke={theme.palette.primary.main} 
                    strokeWidth={2}
                    name="Average Score"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="pass_rate" 
                    stroke={theme.palette.success.main} 
                    strokeWidth={2}
                    name="Pass Rate"
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>
        
        {/* Subject Performance */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Subject Performance
            </Typography>
            {safeSubjectPerformance.length === 0 ? (
              <Box textAlign="center" py={4}>
                <Typography variant="body1" color="text.secondary">
                  No subject performance data available for the selected time range.
                </Typography>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={safeSubjectPerformance}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ test__subject__name, avg_score }) => `${test__subject__name}: ${avg_score.toFixed(1)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="avg_score"
                  >
                    {safeSubjectPerformance.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [`${value.toFixed(1)}%`, 'Average Score']} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Student Performance Table */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Individual Student Performance
        </Typography>
        {safeStudentPerformance.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Typography variant="body1" color="text.secondary">
              No student performance data available for the selected time range.
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Student Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell align="center">Tests Taken</TableCell>
                  <TableCell align="center">Pass Rate</TableCell>
                  <TableCell align="center">Average Score</TableCell>
                  <TableCell align="center">Last Activity</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {safeStudentPerformance.map((student, index) => (
                  <TableRow key={student?.student_id || index} hover>
                    <TableCell>{student?.student_name || 'Unknown Student'}</TableCell>
                    <TableCell>{student?.student_email || 'No email'}</TableCell>
                    <TableCell align="center">{student?.total_attempts || 0}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={`${(student?.pass_rate || 0).toFixed(1)}%`}
                        color={(student?.pass_rate || 0) >= 80 ? 'success' : (student?.pass_rate || 0) >= 60 ? 'warning' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={`${(student?.average_score || 0).toFixed(1)}%`}
                        color={(student?.average_score || 0) >= 80 ? 'success' : (student?.average_score || 0) >= 60 ? 'warning' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">{student?.last_activity || 'Unknown'}</TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        startIcon={<Visibility />}
                        onClick={() => handleStudentClick(student?.student_id)}
                        disabled={!student?.student_id}
                      >
                        View Details
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Subject Performance Breakdown */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Subject Performance Breakdown
        </Typography>
        {safeSubjectPerformance.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Typography variant="body1" color="text.secondary">
              No subject performance data available for the selected time range.
            </Typography>
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={safeSubjectPerformance}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="test__subject__name" />
              <YAxis />
              <Tooltip formatter={(value, name) => [value, name === 'avg_score' ? 'Score (%)' : 'Count']} />
              <Legend />
              <Bar dataKey="avg_score" fill={theme.palette.primary.main} name="Average Score" />
              <Bar dataKey="test_count" fill={theme.palette.secondary.main} name="Total Tests" />
              <Bar dataKey="student_count" fill={theme.palette.success.main} name="Active Students" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Paper>

      {/* Recent Activity */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Recent Class Activity
        </Typography>
        {safeRecentActivity.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Typography variant="body1" color="text.secondary">
              No recent activity data available for the selected time range.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {safeRecentActivity.map((activity, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" fontWeight="medium" noWrap>
                      {activity?.student_name || 'Unknown Student'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {activity?.test_title || 'Unknown Test'}
                    </Typography>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                      <Chip
                        label={`${activity?.score || 0}%`}
                        color={activity?.passed ? 'success' : 'error'}
                        size="small"
                      />
                      <Typography variant="caption" color="text.secondary">
                        {activity?.date || 'Unknown Date'}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>

      {/* Student Detail Dialog */}
      <Dialog
        open={studentDetailDialog}
        onClose={closeStudentDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Student Performance Details
          {selectedStudentData && (
            <Typography variant="subtitle2" color="text.secondary">
              {selectedStudentData?.student_info?.name || 'Unknown Student'} - Last {timeRange} days
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {selectedStudentData && (
            <Box>
              {/* Quick Summary */}
              <Grid container spacing={2} mb={3}>
                <Grid item xs={6} md={3}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h6">{selectedStudentData?.overall_performance?.total_attempts || 0}</Typography>
                      <Typography variant="caption">Total Tests</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h6">{(selectedStudentData?.overall_performance?.pass_fail_breakdown?.pass_percentage || 0).toFixed(1)}%</Typography>
                      <Typography variant="caption">Pass Rate</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h6">{(selectedStudentData?.overall_performance?.average_score || 0).toFixed(1)}%</Typography>
                      <Typography variant="caption">Average Score</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h6">{selectedStudentData?.topic_difficulty_breakdown?.topic_mastery?.length || 0}</Typography>
                      <Typography variant="caption">Topics Covered</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Top Topics */}
              <Typography variant="h6" gutterBottom>
                Top Performing Topics
              </Typography>
              <Box mb={3}>
                {(selectedStudentData?.topic_difficulty_breakdown?.topic_mastery || []).slice(0, 5).map((topic, index) => (
                  <Box key={topic?.topic || index} mb={1}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2">
                        {topic?.topic || 'Unknown Topic'}
                      </Typography>
                      <Chip
                        label={`${topic?.accuracy_percentage || 0}% (${topic?.total_questions || 0} questions)`}
                        color={(topic?.accuracy_percentage || 0) >= 80 ? 'success' : (topic?.accuracy_percentage || 0) >= 60 ? 'warning' : 'error'}
                        size="small"
                      />
                    </Box>
                  </Box>
                ))}
              </Box>

              {/* Recent Activity */}
              <Typography variant="h6" gutterBottom>
                Recent Test Activity
              </Typography>
              <Box>
                {(selectedStudentData?.recent_activity || []).slice(0, 3).map((activity, index) => (
                  <Box key={index} mb={1}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2">
                        {activity?.test_title || 'Unknown Test'}
                      </Typography>
                      <Chip
                        label={`${activity?.score || 0}%`}
                        color={activity?.passed ? 'success' : 'error'}
                        size="small"
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {activity?.completed_at || 'Unknown Date'}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeStudentDialog}>Close</Button>
          <Button 
            startIcon={<Download />}
            onClick={() => {
              // TODO: Implement export functionality
              console.log('Export student data');
            }}
          >
            Export Data
          </Button>
        </DialogActions>
      </Dialog>
      </Container>
  );
};

export default TeacherDashboardAnalytics;
